import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  Fade,
  Tooltip,
  useTheme,
  alpha,
  TableContainer,
} from "@mui/material";
import {
  Close as CloseIcon,
  SkipNext as SkipNextIcon,
  Redo as RedoIcon,
  Undo as UndoIcon,
  FirstPage as FirstPageIcon,
  History as HistoryIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";

const MoveLog = ({
  isMobileView,
  t,
  getMoveHistory,
  getCurrentMoveIndex,
  setCurrentMoveIndex,
  game,
  setFen,
  updateCapturedPieces,
  disableClick,
  onClose,
  selectedGame,
}) => {
  const theme = useTheme();
  const [localIndex, setLocalIndex] = useState(0);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const moveHistory = getMoveHistory();
  const scrollRef = useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (disableClick) return;

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          handleBackward();
          break;
        case "ArrowDown":
          event.preventDefault();
          handleForward();
          break;
        case "Home":
          event.preventDefault();
          handleGoToStart();
          break;
        case "End":
          event.preventDefault();
          handleGoToLast();
          break;
        default:
          break;
      }
    };

    // Add event listener to the document
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [localIndex, moveHistory.length, disableClick]);

  // Load latest move initially
  useEffect(() => {
    if (!disableClick && moveHistory.length > 0) {
      const lastIndex = moveHistory.length - 1;
      const lastFen = moveHistory[lastIndex]?.fen;
      if (lastFen) {
        try {
          game.load(lastFen);
          setCurrentMoveIndex(lastIndex);
          setFen(lastFen);
          updateCapturedPieces();
          setLocalIndex(lastIndex);
        } catch (err) {
          console.error("FEN error:", err);
        }
      }
    }
  }, []);

  // Scroll to bottom when new move added and show scroll hint
  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        if (moveHistory.length > 5) {
          setShowScrollHint(true);
          setTimeout(() => setShowScrollHint(false), 3000);
        }
      });
    }
  }, [moveHistory.length]);

  const jumpTo = (index) => {
    const target = moveHistory[index];
    if (target?.fen) {
      game.load(target.fen);
      setCurrentMoveIndex(index);
      setFen(target.fen);
      updateCapturedPieces();
      setLocalIndex(index);
    }
  };

  const handleForward = () => {
    if (localIndex < moveHistory.length - 1) {
      jumpTo(localIndex + 1);
    }
  };

  const handleBackward = () => {
    if (localIndex > 0) {
      jumpTo(localIndex - 1);
    }
  };

  const handleGoToStart = () => {
    jumpTo(0);
  };

  const handleGoToLast = () => {
    jumpTo(moveHistory.length - 1);
    if (selectedGame) {
      Swal.fire({
        title: t(selectedGame.gameStatus),
        icon: "info",
        confirmButtonText: t("OK"),
      });
    }
  };

  const scrollToTop = () => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToBottom = () => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  };

  const getPieceIcon = (color) => {
    return color === "white" || color === "w" ? "♔" : "♚";
  };

  const getMoveNumber = (index) => {
    return Math.ceil(index / 2);
  };

  const isWhiteMove = (entry) => {
    return entry.color === "w" || entry.color === "white";
  };

  return (
    <Card
      elevation={8}
      sx={{
        width: "100%",
        height: "541px", // Board height - typically 600px for standard chess boards
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        overflow: "hidden",
        position: "relative",
        border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[12],
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
          color: "white",
          p: 2,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "64px",
          flexShrink: 0,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.5,
            }}
          >
            <HistoryIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              {t("Move Log")}
            </Typography>
          </Box>

          {/* Total Moves Under Title */}
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              opacity: 0.9,
              fontWeight: 500,
            }}
          >
            {t("Move_total")} {moveHistory.length - 1}
          </Typography>

          {/* Keyboard Shortcuts Hint */}
          {/* <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              opacity: 0.7,
              fontWeight: 400,
              mt: 0.5,
              display: "block",
            }}
          >
            ↑↓ Navigate • Home/End Jump
          </Typography> */}
        </Box>

        {/* Close Button */}
        <Tooltip title={t("Close")} arrow>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#ffffff",
              bgcolor: alpha("#ffffff", 0.1),
              "&:hover": {
                bgcolor: alpha("#ffffff", 0.2),
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{ display: "flex", flexDirection: "column", position: "relative" }}
      >
        {/* Scroll Controls */}
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Tooltip title={t("Scroll to top")} placement="left" arrow>
            <IconButton
              onClick={scrollToTop}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("Scroll to bottom")} placement="left" arrow>
            <IconButton
              onClick={scrollToBottom}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Scroll Hint */}
        <Fade in={showScrollHint}>
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 15,
              bgcolor: theme.palette.info.main,
              color: "white",
              px: 2,
              py: 1,
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: 600,
              boxShadow: theme.shadows[4],
            }}
          >
            {t("Scroll to see all moves")}
          </Box>
        </Fade>

        {/* Scrollable Table Container */}
        <TableContainer
          ref={scrollRef}
          sx={{
            height: "400px", // Increased from 320px to accommodate the larger total height
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: alpha(theme.palette.grey[300], 0.3),
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: alpha(theme.palette.primary.main, 0.6),
              borderRadius: "4px",
              "&:hover": {
                background: alpha(theme.palette.primary.main, 0.8),
              },
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: theme.palette.primary.main, // Solid background instead of alpha
                    color: "#ffffff", // White text for better contrast
                    borderBottom: `2px solid ${theme.palette.primary.dark}`,
                    fontSize: "0.9rem",
                    width: "80px",
                    position: "sticky",
                    top: 0,
                    zIndex: 100, // Higher z-index to ensure it stays on top
                  }}
                >
                  {t("#")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: theme.palette.primary.main, // Solid background
                    color: "#ffffff",
                    borderBottom: `2px solid ${theme.palette.primary.dark}`,
                    fontSize: "0.9rem",
                    width: "120px",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  {t("Move")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: theme.palette.primary.main, // Solid background
                    color: "#ffffff",
                    borderBottom: `2px solid ${theme.palette.primary.dark}`,
                    fontSize: "0.9rem",
                    width: "100px",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  {t("Player")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: theme.palette.primary.main, // Solid background
                    color: "#ffffff",
                    borderBottom: `2px solid ${theme.palette.primary.dark}`,
                    fontSize: "0.9rem",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  {t("Notation")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {moveHistory.map((entry, index) => {
                const move = entry.move || "";
                const isStart = index === 0;
                const isSelected = localIndex === index;
                const isWhite = isWhiteMove(entry);
                const moveNumber = getMoveNumber(index);

                return (
                  <TableRow
                    key={index}
                    hover={!disableClick}
                    selected={isSelected}
                    onClick={() => !disableClick && jumpTo(index)}
                    sx={{
                      cursor: disableClick ? "default" : "pointer",
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.15)
                        : index % 2 === 0
                          ? alpha(theme.palette.grey[50], 0.5)
                          : "transparent",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": !disableClick && {
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.25)
                          : alpha(theme.palette.primary.main, 0.08),
                        transform: "scale(1.01)",
                      },
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : "2px solid transparent",
                      borderRadius: isSelected ? "8px" : "0px",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected
                          ? theme.palette.primary.main
                          : "inherit",
                        fontSize: "0.85rem",
                        padding: "8px",
                      }}
                    >
                      {!isStart ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {moveNumber}
                          </Typography>
                          {!isWhite && (
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              ...
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <></>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: "0.85rem",
                        padding: "8px",
                      }}
                    >
                      {!isStart ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontFamily: "monospace",
                            fontSize: "0.9rem",
                          }}
                        >
                          <Chip
                            label={move.slice(0, 2)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.7rem",
                              height: "20px",
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                            }}
                          />
                          <Typography variant="body2" sx={{ mx: 0.5 }}>
                            →
                          </Typography>
                          <Chip
                            label={move.slice(3, 5)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.7rem",
                              height: "20px",
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ opacity: 0.6 }}>
                          {t("Initial Position")}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ padding: "8px" }}>
                      {isStart ? (
                        <></>
                      ) : (
                        <Chip
                          icon={
                            <span style={{ fontSize: "1rem" }}>
                              {getPieceIcon(entry.color)}
                            </span>
                          }
                          label={isWhite ? t("White") : t("Black")}
                          size="small"
                          color={isWhite ? "default" : "primary"}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            fontSize: "0.7rem",
                            height: "24px",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: "0.85rem",
                        padding: "8px",
                      }}
                    >
                      {entry.san || (!isStart ? move : "—")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Navigation Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            p: 2,
            borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(10px)",
            flexShrink: 0,
          }}
        >
          {!disableClick && (
            <>
              <Button
                onClick={handleGoToStart}
                disabled={localIndex === 0}
                variant="contained"
                size="small"
                startIcon={<FirstPageIcon />}
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              ></Button>

              <Button
                onClick={handleBackward}
                disabled={localIndex === 0}
                variant="contained"
                size="small"
                startIcon={<UndoIcon />}
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              ></Button>

              <Button
                onClick={handleForward}
                disabled={localIndex === moveHistory.length - 1}
                variant="contained"
                size="small"
                endIcon={<RedoIcon />}
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              ></Button>

              <Button
                onClick={handleGoToLast}
                disabled={localIndex === moveHistory.length - 1}
                variant="contained"
                size="small"
                startIcon={<SkipNextIcon />}
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:disabled": {
                    opacity: 0.5,
                  },
                }}
              ></Button>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default MoveLog;
